import React, { useState, useEffect } from 'react';
import {
  Brain,
  Camera,
  Upload,
  FileText,
  Zap,
  Shield,
  Clock,
  Star,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Eye,
  Heart,
  Activity,
  Microscope,
  Scan,
  Target,
  Download,
  Share2,
  History,
  X,
  Play,
  Pause,
  RotateCcw,
  ZoomIn,
  Info,
  Calendar,
  User
} from 'lucide-react';

const AIDiagnostics = () => {
  const [activeTab, setActiveTab] = useState('scan');
  const [selectedTool, setSelectedTool] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState('dog');

  const diagnosticTools = [
    {
      id: 1,
      name: 'Skin Condition Analysis',
      description: 'AI-powered analysis of skin conditions, rashes, and lesions',
      icon: Eye,
      accuracy: '94%',
      processingTime: '30 seconds',
      supportedFormats: ['JPG', 'PNG', 'HEIC'],
      price: 149,
      features: ['Instant analysis', 'Multiple condition detection', 'Severity assessment', 'Treatment recommendations']
    },
    {
      id: 2,
      name: 'X-Ray Analysis',
      description: 'Advanced bone fracture and joint condition detection',
      icon: Scan,
      accuracy: '96%',
      processingTime: '45 seconds',
      supportedFormats: ['DICOM', 'JPG', 'PNG'],
      price: 199,
      features: ['Fracture detection', 'Joint analysis', 'Bone density assessment', 'Comparative analysis']
    },
    {
      id: 3,
      name: 'Symptom Checker',
      description: 'Comprehensive symptom analysis and potential diagnosis',
      icon: Activity,
      accuracy: '89%',
      processingTime: '15 seconds',
      supportedFormats: ['Text Input'],
      price: 99,
      features: ['Multi-symptom analysis', 'Risk assessment', 'Urgency level', 'Specialist recommendations']
    },
    {
      id: 4,
      name: 'Lab Report Analysis',
      description: 'Intelligent interpretation of blood work and lab results',
      icon: Microscope,
      accuracy: '97%',
      processingTime: '20 seconds',
      supportedFormats: ['PDF', 'JPG', 'PNG'],
      price: 179,
      features: ['Comprehensive analysis', 'Trend identification', 'Reference comparison', 'Health insights']
    },
    {
      id: 5,
      name: 'Behavioral Assessment',
      description: 'AI analysis of pet behavior patterns and concerns',
      icon: Brain,
      accuracy: '91%',
      processingTime: '25 seconds',
      supportedFormats: ['Video', 'Text'],
      price: 129,
      features: ['Behavior pattern analysis', 'Stress indicators', 'Training recommendations', 'Wellness tips']
    },
    {
      id: 6,
      name: 'Dental Health Scanner',
      description: 'Oral health assessment and dental condition detection',
      icon: Target,
      accuracy: '93%',
      processingTime: '35 seconds',
      supportedFormats: ['JPG', 'PNG', 'MOV'],
      price: 159,
      features: ['Dental disease detection', 'Gum health assessment', 'Cleaning recommendations', 'Preventive care tips']
    }
  ];

  const recentAnalyses = [
    {
      id: 1,
      toolName: 'Skin Condition Analysis',
      petName: 'Max',
      date: '2 hours ago',
      result: 'Mild dermatitis detected',
      confidence: '92%',
      status: 'completed'
    },
    {
      id: 2,
      toolName: 'X-Ray Analysis',
      petName: 'Luna',
      date: '1 day ago',
      result: 'No fractures detected',
      confidence: '98%',
      status: 'completed'
    },
    {
      id: 3,
      toolName: 'Symptom Checker',
      petName: 'Buddy',
      date: '3 days ago',
      result: 'Possible ear infection',
      confidence: '87%',
      status: 'reviewed'
    }
  ];

  const animalTypes = [
    { id: 'dog', name: 'Dog', icon: '🐕' },
    { id: 'cat', name: 'Cat', icon: '🐱' },
    { id: 'bird', name: 'Bird', icon: '🐦' },
    { id: 'rabbit', name: 'Rabbit', icon: '🐰' },
    { id: 'hamster', name: 'Hamster', icon: '🐹' },
    { id: 'livestock', name: 'Livestock', icon: '🐄' }
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const startAnalysis = () => {
    if (!uploadedFile || !selectedTool) return;
    
    setIsAnalyzing(true);
    
    // Simulate AI analysis
    setTimeout(() => {
      setAnalysisResult({
        condition: 'Mild Dermatitis',
        confidence: '92%',
        severity: 'Low',
        recommendations: [
          'Apply topical treatment as prescribed',
          'Monitor for 7-10 days',
          'Avoid allergens and irritants',
          'Schedule follow-up if no improvement'
        ],
        referral: 'Consider dermatology specialist if symptoms persist'
      });
      setIsAnalyzing(false);
      setShowResultModal(true);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">AI Diagnostics</h1>
                <p className="text-sm text-gray-500">Advanced AI-powered veterinary diagnostics</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-purple-100 px-3 py-1 rounded-full">
                <Zap className="w-4 h-4 text-purple-600" />
                <span className="text-purple-700 text-sm font-medium">AI Powered</span>
              </div>
              <div className="flex items-center space-x-2 bg-green-100 px-3 py-1 rounded-full">
                <Shield className="w-4 h-4 text-green-600" />
                <span className="text-green-700 text-sm font-medium">HIPAA Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm">
            <button
              onClick={() => setActiveTab('scan')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'scan'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:text-purple-600'
              }`}
            >
              AI Scan
            </button>
            <button
              onClick={() => setActiveTab('results')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'results'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:text-purple-600'
              }`}
            >
              Recent Results
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-700 hover:text-purple-600'
              }`}
            >
              Analysis History
            </button>
          </div>
        </div>

        {/* AI Scan Tab */}
        {activeTab === 'scan' && (
          <div className="space-y-6">
            {/* Animal Selection */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Animal Type</h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
                {animalTypes.map(animal => (
                  <button
                    key={animal.id}
                    onClick={() => setSelectedAnimal(animal.id)}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      selectedAnimal === animal.id
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    <div className="text-2xl mb-2">{animal.icon}</div>
                    <div className="text-sm font-medium text-gray-900">{animal.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Diagnostic Tools */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {diagnosticTools.map(tool => (
                <div
                  key={tool.id}
                  className={`bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                    selectedTool?.id === tool.id ? 'ring-2 ring-purple-600' : ''
                  }`}
                  onClick={() => setSelectedTool(tool)}
                >
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                        <tool.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{tool.name}</h3>
                        <p className="text-2xl font-bold text-purple-600">₹{tool.price}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{tool.description}</p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Accuracy</span>
                        <span className="font-medium text-green-600">{tool.accuracy}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Processing Time</span>
                        <span className="font-medium">{tool.processingTime}</span>
                      </div>
                    </div>
                    <div className="border-t pt-4">
                      <div className="flex flex-wrap gap-1">
                        {tool.supportedFormats.map(format => (
                          <span key={format} className="text-xs bg-gray-100 px-2 py-1 rounded">
                            {format}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Upload Section */}
            {selectedTool && (
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload for Analysis</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  {uploadedFile ? (
                    <div className="space-y-4">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                      <div>
                        <p className="text-lg font-medium text-gray-900">{uploadedFile.name}</p>
                        <p className="text-sm text-gray-600">File uploaded successfully</p>
                      </div>
                      <div className="flex justify-center space-x-4">
                        <button
                          onClick={() => setUploadedFile(null)}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Remove
                        </button>
                        <button
                          onClick={startAnalysis}
                          disabled={isAnalyzing}
                          className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                        >
                          {isAnalyzing ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Analyzing...</span>
                            </>
                          ) : (
                            <>
                              <Brain className="w-4 h-4" />
                              <span>Start Analysis</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto" />
                      <div>
                        <p className="text-lg font-medium text-gray-900">Upload your file</p>
                        <p className="text-sm text-gray-600">
                          Supported formats: {selectedTool.supportedFormats.join(', ')}
                        </p>
                      </div>
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        accept=".jpg,.jpeg,.png,.pdf,.dcm,.mov,.mp4"
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Choose File
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recent Results Tab */}
        {activeTab === 'results' && (
          <div className="space-y-6">
            {recentAnalyses.map(analysis => (
              <div key={analysis.id} className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{analysis.toolName}</h3>
                      <p className="text-sm text-gray-600">Pet: {analysis.petName}</p>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mt-1">
                        <div className="flex items-center space-x-1">
                          <Clock className="w-4 h-4" />
                          <span>{analysis.date}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="w-4 h-4" />
                          <span>Confidence: {analysis.confidence}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="text-right">
                      <div className="font-medium text-gray-900">{analysis.result}</div>
                      <div className={`text-xs px-2 py-1 rounded-full ${
                        analysis.status === 'completed' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {analysis.status}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button className="p-2 text-gray-400 hover:text-gray-600">
                        <Download className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-gray-400 hover:text-gray-600">
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Analysis History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="text-center py-12">
              <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete analysis history</h3>
              <p className="text-gray-600">View detailed history of all your AI diagnostic analyses</p>
              <button className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
                View Full History
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Modal */}
      {showResultModal && analysisResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Analysis Results</h3>
                <button
                  onClick={() => setShowResultModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Result Summary */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{analysisResult.condition}</h4>
                    <p className="text-sm text-gray-600">AI Confidence: {analysisResult.confidence}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="w-4 h-4 text-orange-500" />
                    <span className="text-sm">Severity: {analysisResult.severity}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm">Analysis Complete</span>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="mb-6">
                <h5 className="font-semibold text-gray-900 mb-3">Recommendations</h5>
                <div className="space-y-2">
                  {analysisResult.recommendations.map((rec, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                      <span className="text-sm text-gray-700">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Referral Note */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <div className="flex items-start space-x-3">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5" />
                  <div>
                    <h6 className="font-medium text-blue-900">Professional Referral</h6>
                    <p className="text-sm text-blue-800 mt-1">{analysisResult.referral}</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowResultModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2">
                  <Download className="w-4 h-4" />
                  <span>Download Report</span>
                </button>
                <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
                  <Share2 className="w-4 h-4" />
                  <span>Share with Vet</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIDiagnostics;